<template>
  <div class="flex-grow">
    <template v-if="editable">
      <o-description-details :term="$t('components.recurrence.elements.reservation.repeatEvery')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
        <div class="flex items-center space-x-1">
          <o-field-group v-if="!isRecurrenceIntervalYear" class="flex-grow w-1/3">
            <number-field v-model="_recurrence.repeat.frequency" slim base-class="h-9 flex items-center" />
          </o-field-group>
          <o-field-group class="flex-grow w-2/3">
            <select-field
              v-model="_recurrence.repeat.interval"
              :options="recurrenceIntervalOptions"
              base-class="h-9 flex items-center"
              @change="handleRecurrenceIntervalChange"
            />
          </o-field-group>
        </div>
      </o-description-details>

      <o-description-details v-if="isRecurrenceIntervalWeek" :term="$t('components.recurrence.elements.reservation.repeatOn')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
        <o-field-group>
          <segment-field
            v-model="_recurrence.repeat.on.week.days[0]"
            :options="recurrenceDayOptions"
          />
        </o-field-group>
      </o-description-details>

      <o-description-details v-if="isRecurrenceIntervalMonth" :term="$t('components.recurrence.elements.reservation.repeatOn')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
        <o-field-group>
          <radio-group-field
            v-model="_recurrence.repeat.on.month.type"
            :options="recurrenceMonthTypeOptions"
          />
        </o-field-group>

        <template v-if="isRecurrenceMonthTypeDay">
          <p class="pt-2 pb-1 text-sm text-gray-600 dark:text-gray-300">
            {{ $t('components.recurrence.elements.reservation.monthlyOnThe') }}
          </p>
          <div class="flex items-center space-x-2 w-full">
            <o-field-group class="w-16">
              <select-field
                v-model="_recurrence.repeat.on.month.weeks[0]"
                :options="recurrenceWeekOptions"
                class="mb-0"
              />
            </o-field-group>
            <o-field-group class="flex-grow">
              <segment-field
                v-model="_recurrence.repeat.on.month.days[0]"
                :options="recurrenceDayOptions"
              />
            </o-field-group>
          </div>
        </template>

        <template v-if="isRecurrenceMonthTypeDate">
          <p class="pt-2 pb-1 text-sm text-gray-600 dark:text-gray-300">
            {{ $t('components.recurrence.elements.reservation.monthlyOnThe') }}
          </p>
          <o-field-group class="w-full">
            <select-field
              v-model="_recurrence.repeat.on.month.dates[0]"
              :options="recurrenceDateOptions"
              class="mb-0"
            />
          </o-field-group>
        </template>
      </o-description-details>

      <o-description-details v-if="isRecurrenceIntervalYear" :term="$t('components.recurrence.elements.reservation.repeatOn')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
        <div class="flex items-center space-x-2 w-full">
          <o-field-group class="flex-grow">
            <select-field
              v-model="_recurrence.repeat.on.year.date.month"
              :options="recurrenceMonthOptions"
              class="mb-0"
              @change="handleRecurrenceIntervalYearMonthChange"
            />
          </o-field-group>

          <o-field-group class="flex-grow">
            <select-field
              v-model="_recurrence.repeat.on.year.date.date"
              :options="recurrenceDateOptions"
              class="mb-0"
            />
          </o-field-group>
        </div>
      </o-description-details>

      <o-description-details :term="$t('components.recurrence.elements.reservation.ending')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
        <o-field-group>
          <radio-group-field
            v-model="_recurrence.ending.type"
            :options="recurrenceEndingTypeOptions"
          />
        </o-field-group>
      </o-description-details>

      <o-description-details v-if="isEndingOnDate" :term="$t('components.recurrence.elements.reservation.endingDate')" date term-col="sm:col-span-2" definition-col="sm:col-span-4">
        <o-field-group>
          <date-field
            v-model="_recurrence.ending.date"
            :augmenter="Augmenters.YMD"
          />
        </o-field-group>
      </o-description-details>

      <o-description-details v-if="isEndingAfterRepeats" :term="$t('reservation.afterNumberOfRepeats')" term-col="sm:col-span-2" definition-col="sm:col-span-4">
        <o-field-group>
          <number-field
            v-model="_recurrence.ending.repeats"
            base-class="h-9 flex items-center"
          />
        </o-field-group>
      </o-description-details>
    </template>

    <template v-if="_preview && _preview.recurrence.selectedDates">
      <div v-if="editable" class="flex items-center justify-between mb-3 mt-6">
        <div>
          <h4 class="font-medium text-gray-800 dark:text-gray-100">
            {{ $t('components.recurrence.elements.reservation.recurrenceConflicts') }}
          </h4>
          <h4 class="text-sm text-gray-600 dark:text-gray-300">
            {{ $t('components.recurrence.elements.reservation.recurrenceConflictsInfo') }}
          </h4>
        </div>

        <div>
          <o-tooltip :text="showAll ? $t('components.recurrence.elements.reservation.showOnlyConflicts') : $t('components.recurrence.elements.reservation.showAllRecurrenceDates')">
            <toggle-field v-model="showAll" />
          </o-tooltip>
        </div>
      </div>

      <div v-if="editable && hasConflicts" class="flex items-center justify-between mb-2 px-1">
        <p class="text-sm font-medium text-gray-700 dark:text-gray-200">
          {{ $t('components.recurrence.elements.reservation.overrideAll') }}
        </p>

        <segment-field
          v-model="overrideAllOption"
          :options="overrideAllConflictOptions"
          segmented
        />
      </div>

      <div class="bg-gray-100 p-1 rounded-lg border h-48 dark:bg-gray-800/30">
        <o-scroll class="h-full">
          <template v-for="date in _preview.recurrence.selectedDates" :key="date.date">
            <div v-if="showAll || date.hasConflicts" class="p-2 flex items-center justify-between border-b">
              <p class="text-gray-700 text-sm dark:text-gray-200">
                {{ $date.format($date.utc(new Date(date.date * 1000)), $date.presets.long) }}
              </p>

              <div v-if="editable && date.hasConflicts">
                <segment-field v-model="date.option" :options="date.conflictOptions" segmented />
              </div>

              <div v-if="!editable && date.hasConflicts">
                <o-tooltip :text="$t('components.recurrence.elements.reservation.hasConflictsTooltip')">
                  <o-chip variant="warning" flat>
                    {{ $t('components.recurrence.elements.reservation.hasConflicts') }}
                  </o-chip>
                </o-tooltip>
              </div>

              <div v-if="!date.hasConflicts">
                <o-chip variant="success" flat>
                  {{ $t('components.recurrence.elements.reservation.noConflicts') }}
                </o-chip>
              </div>
            </div>
          </template>

          <p v-if="!showAll && !hasConflicts" class="text-sm text-gray-700 text-center p-4 dark:text-gray-200">
            {{ $t('components.recurrence.elements.reservation.recurrenceDateNoConflicts') }}
          </p>
        </o-scroll>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  ODescriptionDetails,
} from '@/components/elements'

import {
  SelectField,
  NumberField,
  SegmentField,
  RadioGroupField,
  RadioField,
  DateField,
  ToggleField,
} from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import {
  RecurrenceIntervalType,
  RecurrenceIntervalTypeLabels,
  RecurrenceDays,
  RecurrenceDaysLabels,
  RecurrenceIntervalMonthType,
  RecurrenceIntervalMonthTypeLabels,
  RecurrenceWeeks,
  RecurrenceWeeksLabels,
  RecurrenceMonths,
  RecurrenceMonthsLabels,
  RecurrenceEndingType,
  RecurrenceEndingTypeLabels,
  RecurrenceConflict,
  RecurrenceConflictLabels,
} from '@/_api/models/recurrence'

const { $date, $i18n } = useNuxtApp()

interface Props {
  recurrence: Record<string, any>
  preview?: Record<string, any> | null
  editable?: boolean
}

interface Emits {
  (e: 'update:recurrence', value: any): void
  (e: 'update:preview', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  preview: null,
  editable: true,
})

const emit = defineEmits<Emits>()

const showAll = ref(true)
const overrideAllOption = ref<any>(null)
const syncingOverrideAllOption = ref(false)

const _recurrence = computed({
  get () {
    return props.recurrence
  },
  set (value) {
    emit('update:recurrence', value)
  },
})

const _preview = computed({
  get () {
    return props.preview
  },
  set (value) {
    emit('update:preview', value)
  },
})

const recurrenceIntervalOptions = computed(() => {
  return Object.values(RecurrenceIntervalType)
    .map((option) => {
      return {
        value: option,
        text: $i18n.t(RecurrenceIntervalTypeLabels[option]),
      }
    })
})

const recurrenceDayOptions = computed(() => {
  return Object.values(RecurrenceDays)
    .filter((value): value is RecurrenceDays => typeof value === 'number')
    .map((option) => {
      const label = RecurrenceDaysLabels[option]
      return {
        value: option,
        text: $i18n.t(label.replace(/^(.*)\.[^.]+$/, '$1') + '.initial'),
      }
    })
})

const recurrenceMonthTypeOptions = computed(() => {
  return Object.values(RecurrenceIntervalMonthType)
    .map((option) => {
      return {
        value: option,
        text: $i18n.t(RecurrenceIntervalMonthTypeLabels[option]),
      }
    })
})

const recurrenceWeekOptions = computed(() => {
  return Object.values(RecurrenceWeeks)
    .filter((value): value is RecurrenceWeeks => typeof value === 'number')
    .map((option) => {
      return {
        value: option,
        text: $i18n.t(RecurrenceWeeksLabels[option]),
      }
    })
})

const recurrenceMonthOptions = computed(() => {
  return Object.values(RecurrenceMonths)
    .filter((value): value is RecurrenceMonths => typeof value === 'number')
    .map((option) => {
      return {
        value: option,
        text: $i18n.t(RecurrenceMonthsLabels[option]),
      }
    })
})

const recurrenceDateOptions = computed(() => {
  let max = 31

  if (isRecurrenceIntervalYear.value) {
    const monthVal = props.recurrence.repeat.on.year?.date?.month
    const month = (typeof monthVal === 'object' ? monthVal?.value : monthVal) || 1
    const days = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
    max = days[month - 1]
  }

  const dates = Array(max).fill(null).map((_, i) => i + 1)

  return dates
    .map((option) => {
      const dayLabel = $date.getOrdinal(option)

      return {
        value: option,
        text: dayLabel,
      }
    })
})

const recurrenceEndingTypeOptions = computed(() => {
  return Object.values(RecurrenceEndingType)
    .map((option) => {
      return {
        value: option,
        text: $i18n.t(RecurrenceEndingTypeLabels[option]),
      }
    })
})

const recurrenceConflictOptions = computed(() => {
  return Object.values(RecurrenceConflict)
    .map((option) => {
      return {
        value: option,
        text: $i18n.t(RecurrenceConflictLabels[option]),
      }
    })
})

const isEndingOnDate = computed(() => _recurrence.value.ending.type === RecurrenceEndingType.date)
const isEndingAfterRepeats = computed(() => _recurrence.value.ending.type === RecurrenceEndingType.repeats)
const isRecurrenceIntervalWeek = computed(() => _recurrence.value.repeat.interval === RecurrenceIntervalType.week)
const isRecurrenceIntervalMonth = computed(() => _recurrence.value.repeat.interval === RecurrenceIntervalType.month)
const isRecurrenceIntervalYear = computed(() => _recurrence.value.repeat.interval === RecurrenceIntervalType.year)
const isRecurrenceMonthTypeDate = computed(() => _recurrence.value.repeat.on.month.type === RecurrenceIntervalMonthType.date)
const isRecurrenceMonthTypeDay = computed(() => _recurrence.value.repeat.on.month.type === RecurrenceIntervalMonthType.day)
const isRecurrenceMonthTypeLast = computed(() => _recurrence.value.repeat.on.month.type === RecurrenceIntervalMonthType.last)

const hasConflicts = computed(() => {
  const selectedDates = _preview.value?.recurrence?.selectedDates

  if (selectedDates) {
    return selectedDates.some((date: any) => {
      return date.hasConflicts
    })
  }

  return ''
})

const conflictDates = computed(() => {
  return _preview.value?.recurrence?.selectedDates?.filter((date: any) => date.hasConflicts) ?? []
})

const isSameConflictOption = (left: any, right: any) => {
  return (left?.value ?? left) === (right?.value ?? right)
}

const overrideAllConflictOptions = computed(() => {
  const dates = conflictDates.value

  if (!dates.length) {
    return []
  }

  return (dates[0].conflictOptions || [])
    .filter(({ value }: any) => dates.every((date: any) =>
      date.options?.some((option: any) => isSameConflictOption(option, value)),
    ))
})

const applyOverrideAll = (option: any) => {
  conflictDates.value.forEach((date: any) => {
    const matchingOption = date.options?.find((candidate: any) => isSameConflictOption(candidate, option))

    if (matchingOption) {
      date.option = matchingOption
    }
  })
}

const syncOverrideAllOption = () => {
  const dates = conflictDates.value
  syncingOverrideAllOption.value = true

  if (!dates.length) {
    overrideAllOption.value = null
  } else {
    const firstOption = dates[0].option
    overrideAllOption.value = firstOption && dates.every((date: any) => isSameConflictOption(date.option, firstOption))
      ? firstOption
      : null
  }

  nextTick(() => {
    syncingOverrideAllOption.value = false
  })
}

watch(overrideAllOption, (option) => {
  if (!syncingOverrideAllOption.value && option) {
    applyOverrideAll(option)
  }
})

watch(() => _preview.value?.recurrence?.selectedDates, syncOverrideAllOption, { deep: true })

function handleRecurrenceIntervalChange () {
  if (isRecurrenceIntervalYear.value) {
    _recurrence.value.repeat.frequency = 1
  }
}

function handleRecurrenceIntervalYearMonthChange (val: any) {
  if (isRecurrenceIntervalYear.value) {
    const date = props.recurrence.repeat.on?.year?.date
    const monthVal = date?.month
    const month = (typeof monthVal === 'object' ? monthVal?.value : monthVal) || 1
    const days = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
    const max = days[month - 1]

    if (date?.date > max) {
      _recurrence.value.repeat.on.year.date.date = max
    }
  }
}
</script>
